import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Modern replacement for the native `window.confirm`. Two flavours of
 * usage:
 *
 * 1. **Imperative via hook** — drop-in for `if (window.confirm(...))`:
 *
 *    ```ts
 *    const confirm = useConfirm();
 *    if (await confirm({ title: 'Delete X?', description: '…' })) { … }
 *    ```
 *
 *    The provider (`<ConfirmDialogProvider />`) must be mounted once near
 *    the app root for both surfaces. The hook never resolves with
 *    `Promise.reject` — closing the dialog without a choice resolves
 *    `false`, matching `window.confirm`'s semantics on Cancel.
 *
 * 2. **Declarative** — render `<ConfirmDialog>` yourself with `open` +
 *    `onOpenChange` if you want full control of the lifecycle. Useful
 *    when the dialog needs to share state with a form, an in-flight
 *    mutation, or non-trivial focus handling.
 */

export interface ConfirmOptions {
  /** Short title shown in the dialog header. */
  title: string;
  /** Optional supporting paragraph below the title. */
  description?: ReactNode;
  /** Label for the confirm button. Defaults to "Continue". */
  confirmLabel?: string;
  /** Label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;
  /**
   * Visual treatment for the confirm button. `destructive` paints it red —
   * use it for delete / revoke / abandon flows where the user should pause.
   */
  variant?: 'default' | 'destructive';
  /**
   * Whether the confirm button should show a spinner + lock the dialog.
   * Caller passes `true` while their mutation is in flight; the dialog
   * stays open and the user can't double-fire.
   */
  busy?: boolean;
}

interface ConfirmController {
  request: (options: ConfirmOptions) => Promise<boolean>;
  setBusy: (busy: boolean) => void;
}

const ConfirmDialogContext = createContext<ConfirmController | null>(null);

/**
 * Mount once near the app root. Holds the single dialog instance the
 * imperative `useConfirm()` hook drives. Putting one provider per surface
 * (admin + portal) is fine — they're separate React trees in practice.
 */
export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const resolve = useCallback((value: boolean) => {
    // Snapshot + clear before invoking so a caller that re-opens the dialog
    // inside their then() doesn't fight a stale resolver.
    const r = resolverRef.current;
    resolverRef.current = null;
    setOpen(false);
    setBusy(false);
    r?.(value);
  }, []);

  const request = useCallback(
    (next: ConfirmOptions) =>
      new Promise<boolean>((resolveOuter) => {
        // If a previous dialog is still open (e.g. caller chained requests
        // without awaiting), resolve the old one with `false` so its
        // awaiter doesn't hang.
        resolverRef.current?.(false);
        resolverRef.current = resolveOuter;
        setOptions(next);
        setBusy(false);
        setOpen(true);
      }),
    [],
  );

  const controller = useMemo<ConfirmController>(() => ({ request, setBusy }), [request]);

  return (
    <ConfirmDialogContext.Provider value={controller}>
      {children}
      <ConfirmDialog
        open={open}
        options={options}
        busy={busy}
        onConfirm={() => resolve(true)}
        onCancel={() => resolve(false)}
        onOpenChange={(next) => {
          if (!next) resolve(false);
        }}
      />
    </ConfirmDialogContext.Provider>
  );
}

/**
 * Imperative confirm. Returns a function that opens the dialog and
 * resolves with `true` (confirm) or `false` (cancel / Escape / overlay
 * click). Throws if the provider isn't mounted so the failure surfaces
 * at dev time instead of silently swallowing the prompt.
 */
export function useConfirm() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error(
      'useConfirm() called without <ConfirmDialogProvider /> in the tree. ' +
        'Mount it near the app root (src/app/layout.tsx and src/app/portal/layout.tsx).',
    );
  }
  return ctx.request;
}

/**
 * Companion hook for marking the active confirm dialog busy from the
 * caller side. Useful when the user clicked Confirm and the async mutation
 * is in flight — the dialog stays open with a spinner so they can't fire
 * twice. Pair with a try/finally in the caller.
 */
export function useConfirmBusy() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error('useConfirmBusy() called without <ConfirmDialogProvider />');
  }
  return ctx.setBusy;
}

// ---- Declarative dialog (used by the provider; also re-exported for
//      callers that want to render the dialog themselves) -------------

export interface ConfirmDialogProps {
  open: boolean;
  options: ConfirmOptions | null;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onOpenChange: (open: boolean) => void;
}

export function ConfirmDialog({
  open,
  options,
  busy,
  onConfirm,
  onCancel,
  onOpenChange,
}: ConfirmDialogProps) {
  if (!options) return null;

  const variant = options.variant ?? 'default';
  const confirmLabel = options.confirmLabel ?? 'Continue';
  const cancelLabel = options.cancelLabel ?? 'Cancel';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            {variant === 'destructive' && (
              <span
                aria-hidden
                className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive"
              >
                <AlertTriangle className="h-5 w-5" />
              </span>
            )}
            <div className="min-w-0 flex-1 space-y-1.5">
              <DialogTitle className="text-base">{options.title}</DialogTitle>
              {options.description ? (
                <DialogDescription className="text-sm leading-relaxed">
                  {options.description}
                </DialogDescription>
              ) : null}
            </div>
          </div>
        </DialogHeader>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
